import { useTheme } from "@mui/material";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import FullLogoPrimary from "@/assets/logos/full-logo-secondary.png";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { ORDER_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useOrderData from "@/hooks/data/use-orders-data";
import useGetCustomerById from "@/hooks/requests/customer/use-get-customer-by-id";
import { preloadOrderPdf } from "@/hooks/requests/orders/use-get-order-pdf";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import { IOrder } from "@/interfaces/order.interfaces";
import { OrderDetailsMessages } from "@/messages/order-details/order-details.messages";

type Props = {
  messages: OrderDetailsMessages;
  order: IOrder | null;
  isPrint: boolean;
};

function Header({ messages, order, isPrint }: Props) {
  const { orderNumberAndDate, salesChannel } = useOrderData({ order });
  const router = useNavigate();
  const { lang } = useLanguage();
  const customerId = useGetCustomerId();
  const _id = Number(customerId) || 0;
  const { data: customer } = useGetCustomerById({ _id });

  const hasNoPermissionToPrint = useHasNoPermission(ORDER_PERMISSIONS.PRINT);
  const {
    margins: { xl, xxl, huge },
  } = useTheme();

  useEffect(() => {
    if (isPrint || hasNoPermissionToPrint || !order?.id) return;

    preloadOrderPdf(order.id);
  }, [isPrint, hasNoPermissionToPrint, order?.id]);

  if (!order) return;

  const salesChannelLabel = salesChannel ? salesChannel : messages.noSalesChannelInformed;

  const displayImage = () => {
    if (customer?.image) return `data:image/jpeg;base64,${customer?.image}`;

    return FullLogoPrimary;
  };

  const logoWidth = xxl + huge;
  const logoHeight = xl * 2;

  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="center">
      <MDBox>
        <MDBox mb={1}>
          {!isPrint && (
            <MDTypography variant="h6" fontWeight="medium">
              {messages.cardTitle}
            </MDTypography>
          )}
        </MDBox>
        <MDTypography component="p" variant="button" color="text">
          {messages.orderLabel}: <b> {String(orderNumberAndDate)}</b>
        </MDTypography>
        <MDTypography component="p" variant="button" fontWeight="regular" color="text">
          {messages.channelLabel}: <b> {salesChannelLabel}</b>
        </MDTypography>
      </MDBox>

      {isPrint ? (
        <img
          src={displayImage()}
          style={{ maxWidth: logoWidth, maxHeight: logoHeight }}
          alt="Customer logo or default logo"
        />
      ) : (
        <MDButton
          variant="gradient"
          color="dark"
          onClick={() => router(ROUTE_URLS.orderPrint(lang, String(order.id)))}
        >
          {messages.printButtonLabel}
        </MDButton>
      )}
    </MDBox>
  );
}

export default Header;
